/**
 * DoserneTripletexConfig — admin page for rotating the Tripletex refresh
 * token without a redeploy.
 *
 * Shows: current status (configured / expired / not set), source (env vs DB),
 * masked preview, last verified timestamp, sync interval.
 *
 * Actions:
 *  - "Test tilkoblingen" → validates the currently-stored token against
 *    Tripletex; success stamps `last_verified_at`.
 *  - "Lagre nytt token" → validates + saves the new token to the DB
 *    singleton `tripletex_config`. Never persists an invalid token.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, ShieldCheck, ShieldAlert, ArrowLeft, RefreshCw, ExternalLink, GitCompareArrows } from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import DoserneShell from "./DoserneShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtWhen(iso) {
  if (!iso) return "aldri";
  try {
    return new Date(iso).toLocaleString("no-NO", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function DoserneTripletexConfig() {
  const { user, loading: authLoading, authHeader } = useDoserneAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newToken, setNewToken] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tripletex/config`, authHeader());
      setConfig(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke laste konfig");
    }
    setLoading(false);
  };

  useEffect(() => { if (user?.is_admin) load(); /* eslint-disable-next-line */ }, [user]);

  const testConnection = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/tripletex/config/test`, {}, authHeader());
      toast.success("Token er gyldig — tilkoblingen fungerer");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Token virker ikke lenger — lim inn et nytt");
    }
    setBusy(false);
  };

  const saveToken = async () => {
    const trimmed = newToken.trim();
    if (!trimmed) {
      toast.warning("Lim inn et refresh token først");
      return;
    }
    if (!trimmed.startsWith("tlxr_")) {
      toast.error("Ugyldig format — Tripletex-token starter med 'tlxr_'");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/tripletex/config`, { refresh_token: trimmed }, authHeader());
      toast.success(`Token lagret og verifisert · ${data.token_preview}`);
      setNewToken("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre token");
    }
    setBusy(false);
  };

  const runFullSync = async () => {
    if (!window.confirm(
      "Kjøre full reconciliation?\n\n" +
      "• Henter alle prosjekter fra Tripletex\n" +
      "• Legger til nye (aktive og lukkede)\n" +
      "• Arkiverer prosjekter som er lukket i Tripletex\n" +
      "• Arkiverer prosjekter som ikke lenger finnes i Tripletex\n\n" +
      "Trykk OK for å fortsette."
    )) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/tripletex/sync`, {}, authHeader());
      toast.success(
        `Full sync ferdig: ${data.created} nye · ` +
        `${data.archived_closed} arkivert (lukket) · ${data.archived_missing} arkivert (fjernet)`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Full sync feilet");
    }
    setBusy(false);
  };

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;
  if (!user.is_admin) return <Navigate to="/doserne" replace />;

  const isConfigured = !!config?.configured;
  const StatusIcon = isConfigured ? ShieldCheck : ShieldAlert;
  const statusColor = isConfigured ? "text-emerald-600 bg-emerald-50 border-emerald-200" : "text-red-600 bg-red-50 border-red-200";

  return (
    <DoserneShell>
      <div className="mb-5">
        <Link to="/doserne/prosjekter" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-3" data-testid="tripletex-config-back">
          <ArrowLeft className="w-3 h-3" /> Tilbake til Prosjekter
        </Link>
        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Tripletex — konfigurasjon</h1>
        <p className="text-sm text-slate-500 mt-1">Roter refresh-tokenet uten redeploy. Auto-sync kjører hver <span className="font-semibold">{Math.round((config?.sync_interval_seconds || 21600) / 3600)}. time</span>.</p>
      </div>

      {loading || !config ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-5">
          {/* STATUS CARD */}
          <div className={`border-2 rounded-2xl p-5 ${statusColor}`} data-testid="tripletex-status-card">
            <div className="flex items-start gap-4">
              <StatusIcon className="w-10 h-10 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-lg font-black">
                  {isConfigured ? "Tripletex tilkoblet" : "Ingen token konfigurert"}
                </div>
                <div className="text-sm mt-1 space-y-0.5">
                  {isConfigured && (
                    <>
                      <div>Token: <span className="font-mono">{config.token_preview}</span> <span className="text-xs opacity-70">({config.source === "db" ? "fra DB" : "fra .env"})</span></div>
                      <div>Sist verifisert: <span className="font-semibold">{fmtWhen(config.last_verified_at)}</span></div>
                      {config.updated_at && <div>Sist oppdatert av: <span className="font-semibold">{config.updated_by || "?"}</span> · {fmtWhen(config.updated_at)}</div>}
                    </>
                  )}
                  {!isConfigured && (
                    <div>Lim inn et refresh-token nedenfor for å aktivere synk.</div>
                  )}
                </div>
              </div>
              {isConfigured && (
                <button
                  onClick={testConnection}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-current text-sm font-semibold hover:bg-current/5 flex-shrink-0"
                  data-testid="tripletex-test-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Test tilkoblingen
                </button>
              )}
            </div>
          </div>

          {/* FULL SYNC CARD */}
          {isConfigured && (
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="tripletex-fullsync-card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-black text-slate-900 mb-1">Full reconciliation</h2>
                  <p className="text-sm text-slate-500">
                    Hent alt fra Tripletex — legger til nye, arkiverer lukkede og fjernede.
                    Bruk kun ved månedlig opprydding; for daglig bruk hold deg til «Sync aktive» på Prosjekter.
                  </p>
                </div>
                <button
                  onClick={runFullSync}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-sm font-black flex-shrink-0"
                  style={{ color: "#fff" }}
                  data-testid="tripletex-fullsync-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompareArrows className="w-4 h-4" />}
                  Kjør full sync
                </button>
              </div>
            </div>
          )}

          {/* NEW TOKEN CARD */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="tripletex-new-token-card">
            <h2 className="text-lg font-black text-slate-900 mb-1">Bytt refresh-token</h2>
            <p className="text-sm text-slate-500 mb-4">
              Tokenet valideres mot Tripletex før det lagres — et ugyldig token lagres aldri.
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 text-xs text-slate-600 space-y-1">
              <div className="font-semibold text-slate-800">Slik henter du et nytt refresh-token:</div>
              <ol className="list-decimal list-inside space-y-0.5 pl-1">
                <li>Logg inn på <a href="https://tripletex.no" target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-0.5">Tripletex <ExternalLink className="w-3 h-3" /></a></li>
                <li>Gå til <span className="font-mono">Company → API-tokens</span> (eller «Selskap → API-nøkler»)</li>
                <li>Trykk «Lag nytt refresh-token», velg minst tilgang til <span className="font-mono">/project</span></li>
                <li>Kopier tokenet — det vises kun én gang</li>
                <li>Lim det inn under og trykk «Lagre og verifiser»</li>
              </ol>
            </div>

            <label className="block text-xs font-black text-slate-700 uppercase tracking-widest mb-1.5">Refresh-token</label>
            <textarea
              value={newToken}
              onChange={(e) => setNewToken(e.target.value)}
              placeholder="tlxr_eyJ..."
              rows={4}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-xs font-mono focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
              data-testid="tripletex-token-input"
              autoComplete="off"
              spellCheck={false}
            />
            <div className="mt-1 text-[10px] text-slate-400">Tokenet starter med <span className="font-mono">tlxr_</span> og er ca. 500 tegn.</div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                onClick={() => setNewToken("")}
                disabled={busy || !newToken}
                className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                data-testid="tripletex-clear-btn"
              >
                Fjern
              </button>
              <button
                onClick={saveToken}
                disabled={busy || !newToken.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-sm font-black"
                style={{ color: "#fff" }}
                data-testid="tripletex-save-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Lagre og verifiser
              </button>
            </div>
          </div>
        </div>
      )}
    </DoserneShell>
  );
}
